import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wand2, Sparkles, Crown, Github, Globe } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BlueprintCard from "../components/BlueprintCard";
import ReadAlongside from "../components/ReadAlongside";
import FollowUpPanel from "../components/FollowUpPanel";
import UpgradeNudge from "../components/UpgradeNudge";
import PlatformPicker from "../components/PlatformPicker";
import MarketingPreview from "../components/MarketingPreview";
import WebPreviewDialog from "../components/WebPreviewDialog";
import RepoToAppleDialog from "../components/RepoToAppleDialog";
import UrlToAppleDialog from "../components/UrlToAppleDialog";
import PushToGitHubDialog from "../components/PushToGitHubDialog";
import FileDropzone from "../components/FileDropzone";
import { toast } from "sonner";

// Each sample carries a curated curriculum pick.  When the learner taps a
// sample the ReadAlongside panel pins that chapter first, so the reading
// feels chosen by Ms. Pradhan rather than by a search box.
const EXAMPLES = [
  {
    prompt: "A journaling app for iPhone with daily reminders, mood tracking, and iCloud sync.",
    code: "CS-KB01-M02-A02",   // Variables, Data, and Decisions
  },
  {
    prompt: "A habit tracker for kids with sticker rewards and a weekly streak chart.",
    code: "CS-KB01-M03-A06",   // Working with Data, Variables, and Expressions
  },
  {
    prompt: "A finance tracker with categories, monthly charts, and CSV export.",
    code: "CS-KB01-M07-A02",   // Building Programs That Work with Collections
  },
  {
    prompt: "A reading log that recommends my next book with tags and a goodreads-style shelf.",
    code: "CS-KB01-M04-A09",   // Data Structures and Algorithmic Thinking
  },
];

export default function Studio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [prompt, setPrompt] = useState("");
  const [bp, setBp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [usage, setUsage] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [marketingPlatforms, setMarketingPlatforms] = useState(["ios"]);
  const [webPreviewOpen, setWebPreviewOpen] = useState(false);
  const [repoOpen, setRepoOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushPlatforms, setPushPlatforms] = useState(["ios"]);
  const [seedUploads, setSeedUploads] = useState([]);
  const [assetUploads, setAssetUploads] = useState([]);
  // First-typing nudge — auto-opens the Read-Alongside panel the very first
  // time a learner starts prompting in Studio, then never bothers them again.
  const [espressoAutoOpen, setEspressoAutoOpen] = useState(false);
  const [pinnedCode, setPinnedCode] = useState(null);
  useEffect(() => {
    if (espressoAutoOpen) return;
    if (prompt.trim().length < 6) return;
    try {
      if (localStorage.getItem("dpa.studio.espresso.discovered") === "1") return;
      localStorage.setItem("dpa.studio.espresso.discovered", "1");
    } catch { /* ignore */ }
    setEspressoAutoOpen(true);
  }, [prompt, espressoAutoOpen]);

  useEffect(() => {
    const seed = params.get("prompt");
    if (seed) setPrompt(seed);
    if (params.get("remix") === "1") {
      try {
        const stash = sessionStorage.getItem("dpa_remix_app");
        if (stash) {
          setBp(JSON.parse(stash));
          sessionStorage.removeItem("dpa_remix_app");
          toast.success("Remix loaded — tweak it and re-share!");
        }
      } catch { /* ignore */ }
    }
    const bpId = params.get("blueprint_id");
    if (bpId && user) {
      api.get(`/prompt/blueprints/${bpId}`).then(({ data }) => {
        setBp(data);
        setSaved(true);
        toast.success(`Resumed: ${data.title}`);
      }).catch(() => toast.error("Could not load that project."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user]);

  useEffect(() => {
    if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  const generate = async (e) => {
    e?.preventDefault();
    if (prompt.trim().length < 10) return toast.error("Say a little more about your app idea.");
    setBusy(true); setBp(null); setSaved(false);
    try {
      const { data } = await api.post("/prompt/generate", {
        prompt,
        upload_ids: seedUploads.map(u => u.id),
      });
      setBp(data);
      toast.success("Blueprint ready ✨");
      if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(err.response?.data?.detail || "You've used all your prompts this month.");
      } else {
        toast.error(err.response?.data?.detail || "Generation failed. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!user) {
      toast("Log in to save your blueprint", { description: "Saving keeps it in your dashboard.", action: { label: "Log in", onClick: () => nav("/login") } });
      return;
    }
    setSaving(true);
    try {
      await api.post("/prompt/blueprints", { blueprint: bp });
      setSaved(true);
      toast.success("Saved to your dashboard");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const sharePreview = async () => {
    if (!user) {
      toast("Log in to share a public preview link.", {
        action: { label: "Log in", onClick: () => nav("/login") },
      });
      return;
    }
    try {
      const { data } = await api.post("/prompt/share-preview", { blueprint: bp });
      const url = `${window.location.origin}/preview/${data.id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Preview link copied", { description: url });
      } catch {
        toast.success("Preview ready", { description: url });
      }
      setBp({ ...bp, id: data.id, is_public: true, saved: true });
      setSaved(true);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not share preview");
    }
  };

  const branchBlueprint = () => {
    if (!bp) return;
    const cloned = {
      ...bp,
      id: undefined,
      saved: false,
      is_public: false,
      shared_at: null,
      branched_from: bp.id,
      title: bp.title?.endsWith("(branch)") ? bp.title : `${bp.title} (branch)`,
      refine_turns: [],
      created_at: new Date().toISOString(),
    };
    setBp(cloned);
    setSaved(false);
    toast.success("Branched — refine it without affecting the original");
  };

  const exportNow = async (platforms, opts = {}) => {
    if (opts.marketing) {
      // Open the preview dialog — it handles gen, regen, share, and download
      setMarketingPlatforms(platforms);
      setPickerOpen(false);
      setMarketingOpen(true);
      return;
    }
    // Sync zip (no marketing) — bundle uploaded asset images into Assets.xcassets
    setExporting(true);
    try {
      const res = await fetch(`${API}/prompt/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("dpa_token") ? { Authorization: `Bearer ${localStorage.getItem("dpa_token")}` } : {}),
        },
        body: JSON.stringify({
          blueprint: bp,
          platforms,
          marketing: false,
          asset_upload_ids: assetUploads.map(u => u.id),
        }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      triggerDownload(blob, bp.title);
      toast.success("Starter kit downloaded");
      setPickerOpen(false);
    } catch {
      toast.error("Could not export");
    } finally {
      setExporting(false);
    }
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "MyApp").replace(/\s+/g, "")}-starter.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-narrow py-12 md:py-20" data-testid="studio-page">
      <div className="text-center mb-10">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2 justify-center">
          <Sparkles className="w-3.5 h-3.5" /> Prompt Studio
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight tracking-tight">
          Describe an app. <span className="italic text-marigold">Get a blueprint.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          Tell us what you want to build. We'll sketch the screens, navigation, schema, App Store description,
          and a real SwiftUI starter — all in about a minute.
        </p>
      </div>

      {usage && (
        <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between text-sm" data-testid="studio-usage">
          <div className="inline-flex items-center gap-2 text-ink2">
            <Crown className="w-4 h-4 text-saffron" />
            <span className="capitalize">{usage.tier}</span> tier ·
            {usage.unlimited ? (
              <span className="text-teal font-medium ml-1">unlimited prompts</span>
            ) : (
              <span className="ml-1">
                <span className="font-medium text-ink">{usage.remaining}</span> of {usage.limit} prompts left this month
              </span>
            )}
          </div>
          {!usage.unlimited && usage.remaining <= 1 && (
            <button onClick={() => nav("/pricing")} className="text-marigold font-medium hover:underline" data-testid="studio-upgrade-link">
              Upgrade →
            </button>
          )}
        </div>
      )}

      <UpgradeNudge usage={usage} variant="app" />

      <form onSubmit={generate} className="card-soft p-5 md:p-6 mb-4" data-testid="studio-form">
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="e.g. Create a journaling app for iPhone with daily reminders, mood tracking, and iCloud sync."
          className="w-full bg-transparent text-ink text-lg placeholder:text-ink2/60 outline-none resize-none"
          data-testid="studio-prompt-textarea"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
          <div className="text-xs text-ink2">
            {prompt.length} / 2000 chars · powered by Claude Sonnet 4.5
          </div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="studio-generate-button">
            <Wand2 className="w-4 h-4" /> {busy ? "Generating…" : "Generate blueprint"}
          </button>
        </div>
      </form>

      {/* Read alongside — chapters from the curriculum that pair with the prompt */}
      <div className="mb-4">
        <ReadAlongside
          seeds={prompt || "apple app swift screens data model state"}
          eyebrow="Read alongside as you build"
          title="chapters from the curriculum for this Apple app"
          askPrompt={prompt ? `As I prompt "${prompt.slice(0, 200)}" in Studio, what curriculum ideas will help me most?` : `What chapters from the curriculum help me prompt a great Apple app in Studio?`}
          defaultOpen={false}
          forceOpen={espressoAutoOpen}
          pinnedCode={pinnedCode}
          testId="studio-read-alongside"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <FileDropzone
          purpose="seed"
          value={seedUploads}
          onChange={setSeedUploads}
          label="References (mockups, PRDs, screenshots)"
          hint="PNG/JPG/PDF/MD · enriches the prompt"
        />
        <FileDropzone
          purpose="asset"
          value={assetUploads}
          onChange={setAssetUploads}
          label="App assets (logo, icons)"
          hint="PNG/JPG/WEBP · bundled into Assets.xcassets"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-8 text-sm">
        <span className="text-ink2">Or skip the prompt and</span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setUrlOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
            data-testid="studio-import-url-button"
          >
            <Globe className="w-4 h-4" /> URL → Apple PWA
          </button>
          <button
            type="button"
            onClick={() => setRepoOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
            data-testid="studio-import-repo-button"
          >
            <Github className="w-4 h-4" /> Import from GitHub → Apple app
          </button>
        </div>
      </div>

      {!bp && !busy && (
        <div className="mb-10">
          <div className="label-eyebrow mb-3">Try one of these</div>
          <div className="flex flex-wrap gap-2" data-testid="studio-examples">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setPrompt(ex.prompt); setPinnedCode(ex.code); setEspressoAutoOpen(true); }}
                className="text-left text-sm px-4 py-2 bg-paper rounded-full border border-border hover:border-saffron hover:bg-white transition"
                data-testid={`studio-example-${i}`}
              >
                {ex.prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {busy && <GeneratingSkeleton />}

      {bp && <BlueprintCard bp={bp} onSave={save} saving={saving} saved={saved} onExport={() => setPickerOpen(true)} onWebPreview={() => setWebPreviewOpen(true)} onPushGitHub={() => setPushOpen(true)} onShare={sharePreview} onBranch={branchBlueprint} />}

      {bp && (
        <div className="mt-6">
          <FollowUpPanel
            blueprint={bp}
            endpoint="/prompt/refine"
            variant="app"
            onUpdate={(newBp) => { setBp(newBp); setSaved(false); }}
            onUsageRefresh={() => user && api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {})}
          />
        </div>
      )}

      <PlatformPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={exportNow}
        busy={exporting}
        title={bp ? `Export "${bp.title}"` : "Export starter kit"}
      />

      <MarketingPreview
        open={marketingOpen}
        onOpenChange={setMarketingOpen}
        blueprint={bp}
        platforms={marketingPlatforms}
      />

      <WebPreviewDialog
        open={webPreviewOpen}
        onOpenChange={setWebPreviewOpen}
        blueprint={bp}
      />

      <RepoToAppleDialog
        open={repoOpen}
        onOpenChange={setRepoOpen}
        onBlueprint={(newBp) => { setBp(newBp); setSaved(false); }}
      />

      <UrlToAppleDialog
        open={urlOpen}
        onOpenChange={setUrlOpen}
        onBlueprint={(newBp) => { setBp(newBp); setSaved(false); }}
      />

      <PushToGitHubDialog
        open={pushOpen}
        onOpenChange={setPushOpen}
        blueprint={bp}
        platforms={pushPlatforms}
      />
    </div>
  );
}

function GeneratingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="studio-generating">
      <div className="card-soft p-7">
        <div className="h-3 w-24 bg-paper rounded mb-3" />
        <div className="h-8 w-2/3 bg-paper rounded mb-3" />
        <div className="h-4 w-full bg-paper rounded mb-2" />
        <div className="h-4 w-5/6 bg-paper rounded" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {[0,1,2,3].map(i => <div key={i} className="card-soft p-5 h-40" />)}
      </div>
      <div className="rounded-2xl bg-codebg p-8 h-60 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" style={{ backgroundSize: "200% 100%" }} />
      </div>
    </div>
  );
}
